import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import ImageWithFallback from '../components/ImageWithFallback';
import { writingsApi, martyrsApi, WRITING_TYPE_LABELS } from '../services/api';
import { excerpt } from '../utils/place';

const WritingsPage = () => {
  const [params] = useSearchParams();
  const martyrId = params.get('id');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [writingType, setWritingType] = useState('');
  const [martyrName, setMartyrName] = useState('');

  useEffect(() => {
    if (!martyrId) {
      setMartyrName('');
      return;
    }
    martyrsApi.getById(martyrId)
      .then((data) => setMartyrName(data.martyr?.name_ar || ''))
      .catch(() => setMartyrName(''));
  }, [martyrId]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await writingsApi.getAll({
          limit: 24,
          ...(search && { search }),
          ...(martyrId && { martyr_id: martyrId }),
          ...(writingType && { writing_type: writingType })
        });
        setItems(data.writings || []);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [search, martyrId, writingType]);

  const title = martyrId ? `كتب عنه${martyrName ? ` — ${martyrName}` : ''}` : 'كتب عنه';
  const subtitle = martyrId
    ? `كتابات ومقالات أضيفت عن هذا الشهيد من لوحة الإدارة.`
    : 'كل الكتابات في الأرشيف: المقالات والكتب والخاطرات العامة، وما كُتب عن شهيد معيّن.';

  return (
    <Layout>
      <Seo title={title} description={subtitle} />
      <PageHero eyebrow={martyrId ? 'عن الشهيد' : 'مكتبة الأرشيف'} title={title} subtitle={subtitle} />
      <div className="container-responsive py-12">
        <div className="mb-10 grid gap-4 rounded-3xl bg-white p-5 shadow-soft dark:bg-stone-900 md:grid-cols-2">
          <input className="form-input" placeholder="ابحث في العنوان أو الكاتب" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="form-select" value={writingType} onChange={(e) => setWritingType(e.target.value)}>
            <option value="">كل التصنيفات</option>
            {Object.entries(WRITING_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        {loading ? <p>جاري التحميل...</p> : (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {items.map((item) => (
              <Link key={item.id} to={`/writings/${item.id}`} className="group overflow-hidden rounded-3xl bg-white shadow-soft transition hover:-translate-y-1 hover:shadow-large dark:bg-stone-900">
                <div className="h-44 overflow-hidden bg-stone-200">
                  <ImageWithFallback src={item.cover_url || item.martyr_image_url || '/default.png'} alt={item.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                </div>
                <div className="space-y-2 p-6">
                  <span className="text-xs text-gold-700">{WRITING_TYPE_LABELS[item.writing_type]}</span>
                  <h2 className="text-xl font-bold">{item.title}</h2>
                  <p className="text-sm text-stone-500">
                    {item.author}
                    {item.martyr_name_ar ? ` — عن ${item.martyr_name_ar}` : ' — كتابة عامة'}
                  </p>
                  <p className="text-stone-600 dark:text-stone-300">{excerpt(item.content, 120)}</p>
                  <span className="text-sm font-semibold text-primary-800">اقرأ المزيد ←</span>
                </div>
              </Link>
            ))}
          </div>
        )}
        {!loading && items.length === 0 && <p className="py-16 text-center text-stone-500">لا توجد كتابات مطابقة</p>}
      </div>
    </Layout>
  );
};

export default WritingsPage;
